/**
 * Live capability probing for template gating (S7-R M1). Two capabilities
 * exist today:
 *  - `'kusto-cluster'`: true for a direct ADX cluster connection, false for
 *    an App Insights / Log Analytics proxy (Docs/DbEngineKusto.md §4.1).
 *    Reuses the existing proxy-detection helper rather than duplicating
 *    URL-sniffing logic.
 *  - `'mysql-not-mariadb'`: true once a fetched server-version string proves
 *    the server is MySQL and NOT MariaDB. MariaDB reports a MySQL-shaped
 *    version string (e.g. `10.11.6-MariaDB`) that would otherwise numerically
 *    satisfy a `minimumVersion` gate it doesn't actually meet feature-wise
 *    (no `performance_schema.data_lock_waits`, no bundled `sys` schema) —
 *    this capability lets a template opt out of MariaDB categorically,
 *    independent of its own `minimumVersion` threshold. Unproven (version
 *    unknown) conservatively does NOT grant the capability, same "never
 *    guess compatible" default as `meetsMinimumVersion`.
 */
import { isKustoProxyConnection } from '../../../connections/services/kustoProxyConnection';
import type { DashboardEngineId } from '../types/dashboardTypes';
import type { DashboardTemplateCapability } from './templateTypes';

const MARIADB_VERSION_PATTERN = /mariadb/i;

export function isMariaDbVersionString(versionString: string): boolean {
  return MARIADB_VERSION_PATTERN.test(versionString);
}

export function templateCapabilities(
  connectionId: string | undefined,
  engine: DashboardEngineId | undefined,
  serverVersion: string | undefined,
): DashboardTemplateCapability[] {
  const capabilities: DashboardTemplateCapability[] = [];

  if (engine === 'kusto' && connectionId && !isKustoProxyConnection(connectionId)) {
    capabilities.push('kusto-cluster');
  }

  if (engine === 'mysql' && serverVersion && !isMariaDbVersionString(serverVersion)) {
    capabilities.push('mysql-not-mariadb');
  }

  return capabilities;
}
